export class PanelRenderer {
  constructor(container, options = {}) {
    this.container = container;
    this.options = {
      panelSide: 'right',
      panelWidth: '300px',
      autoClose: true,
      ...options
    };
    this.panel = null;
    this.data = [];
    this.currentItemId = null;
  }

  create() {
    if (!this.container) return;

    // Create panel HTML
    this.panel = document.createElement('div');
    this.panel.className = 'dw-map-panel';
    this.panel.style.cssText = `
      position: absolute;
      top: 0;
      ${this.options.panelSide}: -${this.options.panelWidth};
      width: ${this.options.panelWidth};
      height: 100%;
      background: white;
      box-shadow: 0 0 10px rgba(0,0,0,0.3);
      z-index: 1000;
      transition: ${this.options.panelSide} 0.3s ease-in-out;
      overflow-y: auto;
      padding: 20px;
      box-sizing: border-box;
    `;

    // Create close button
    const closeBtn = document.createElement('button');
    closeBtn.innerHTML = '×';
    closeBtn.style.cssText = `
      position: absolute;
      top: 10px;
      ${this.options.panelSide === 'right' ? 'left' : 'right'}: 10px;
      background: none;
      border: none;
      font-size: 24px;
      cursor: pointer;
      color: #666;
    `;
    closeBtn.onclick = () => this.hide();

    // Create content area
    const content = document.createElement('div');
    content.className = 'dw-map-panel-content';
    content.style.cssText = `
      margin-top: 30px;
    `;

    this.panel.appendChild(closeBtn);
    this.panel.appendChild(content);
    this.container.appendChild(this.panel);
  }

  setupClickHandling() {
    // Add event delegation for more info links
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('dw-map-more-info-link')) {
        e.preventDefault();
        const itemId = e.target.getAttribute('data-item-id');
        const item = this.data.find(d => d.id == itemId);
        
        if (item) {
          this.showItemDetails(item);
        }
      }
    });

    // Add Esc key handler for closing panel
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isVisible()) {
        this.hide();
      }
    });
  }

  setupWheelHandling() {
    // Prevent mouse wheel events from bubbling to the map when scrolling in the panel
    if (this.panel) {
      this.panel.addEventListener('wheel', (e) => {
        e.stopPropagation();
      }, { passive: true });
    }
  }

  setupMapClickHandling(map) {
    if (!this.options.autoClose) return;

    // Add map click handler for closing panel
    map.on('click', () => {
      if (this.isVisible()) {
        this.hide();
      }
    });
  }

  setData(data) {
    this.data = data;
  }

  async showItemDetails(item) {
    const itemId = item.id || Math.random().toString(36).substr(2, 9);
    this.currentItemId = itemId;
    
    // Debug logging for panel interactions
    if (this.options.debug) {
      this.options.debug.log("Panel item details:", item.title?.rendered || 'Onbekend', "ID:", itemId);
    }
    
    let panelContent;
    
    if (this.options.panelContentProvider) {
      // Use async content provider
      try {
        panelContent = await this.options.panelContentProvider(item);
      } catch (error) {
        console.error('Panel content provider error:', error);
        panelContent = `<div class="dw-map-panel">${this.options.loadErrorMessage}</div>`;
      }
    } else {
      // Use default content
      panelContent = `
        <div class="dw-map-panel">
          <p>Geen definitie voor paneelgegevens</p>
        </div>
      `;
    }
    
    // If panel is already visible, just update content
    if (this.isVisible()) {
      this.setContent(panelContent);
      if (this.options.debug) {
        this.options.debug.log("Panel content updated");
      }
    } else {
      // If panel is not visible, show it
      this.show(panelContent);
      if (this.options.debug) {
        this.options.debug.log("Panel shown");
      }
    }
  }

  show(content = '') {
    if (!this.panel) return;
    
    const contentDiv = this.panel.querySelector('.dw-map-panel-content');
    if (contentDiv) {
      contentDiv.innerHTML = content;
    }
    
    this.panel.style[this.options.panelSide] = '0';
  }

  hide() {
    if (!this.panel) return;
    this.panel.style[this.options.panelSide] = `-${this.options.panelWidth}`;
    
    if (this.options.debug) {
      this.options.debug.log("Panel hidden");
    }
  }

  toggle(content = '') {
    if (!this.panel) return;
    
    const isVisible = this.panel.style[this.options.panelSide] === '0px';
    if (isVisible) {
      this.hide();
    } else {
      this.show(content);
    }
  }

  setContent(content) {
    if (!this.panel) return;
    
    const contentDiv = this.panel.querySelector('.dw-map-panel-content');
    if (contentDiv) {
      contentDiv.innerHTML = content;
    }
  }

  isVisible() {
    if (!this.panel) return false;
    return this.panel.style[this.options.panelSide] === '0px';
  }
}
